import React from 'react';
import Icone from '../primitivos/Icone';

/**
 * Lista de opções para escolher uma (at-58).
 *
 * 🔴 **É o corpo de praticamente todo modal do fluxo de adesão** — marcas,
 * veículos, cores, combustíveis, tipos de veículo, categorias, cotas,
 * cooperativas e regionais: **9 modais** que fazem a mesma coisa e a escreveram
 * cada um do seu jeito (`<ul class="modal-list">` com `<li onClick>` era o mais
 * comum). Este componente é o que permite migrá-los sem repetir a nona versão.
 *
 * ## Por que `<button>` dentro de `<li>`, e não `<li onClick>`
 *
 * O padrão legado (`<li onClick={...}>`) **não é alcançável pelo teclado**: `li`
 * não recebe foco, não responde a Enter e não é anunciado como acionável. Com
 * `<button>`, foco, Enter, Espaço e o anúncio do leitor de tela vêm de graça —
 * mesma razão pela qual o `RadioCards` usa radio nativo em vez de `div`.
 *
 * ⚠️ **Componente burro, e deve continuar:** não busca, não pagina, não filtra e
 * não conhece domínio — quem faz isso é o modal que o usa (o `SearchInput` do kit
 * entra ao lado, quando a lista é longa). Mesma filosofia do `DataList`.
 */

interface PropsListaSelecao<T> {
  itens: readonly T[];
  /** Chave de cada item (o `key` do React). */
  chave: (item: T) => string;
  /** Texto principal da linha. */
  rotulo: (item: T) => string;
  /** Linha secundária, discreta (ex.: "Cuiabá / MT"). Opcional. */
  /**
   * Linha secundária, sob o rótulo.
   *
   * ⚠️ Aceita **nó**, e não só string (at-59): o modal de modelos precisa marcar
   * a motocicleta elétrica que ainda não tem código no SGA — informação que muda
   * o desfecho da adesão e não pode virar texto corrido indistinguível.
   */
  detalhe?: (item: T) => React.ReactNode;
  /**
   * Conteúdo à DIREITA do item — no modal de modelos, o preço de cada versão.
   *
   * 🔴 Existe porque o preço não podia descer para o `detalhe` (at-59): a at-21
   * o colocou ali justamente por ser **fator de decisão** do consultor, e a linha
   * secundária é esmaecida e truncada. Fica à direita, alinhado entre os itens,
   * que é como se compara preço numa lista.
   */
  sufixo?: (item: T) => React.ReactNode;
  aoEscolher: (item: T) => void;
  /**
   * Item atualmente escolhido, para marcá-lo na lista.
   *
   * ⚠️ A marca é **ícone + cor + `aria-current`**, não só cor: o consultor usa o
   * app com sol na tela (mesma regra do `Chip`).
   */
  escolhido?: (item: T) => boolean;
  /** O que mostrar quando não há itens — normalmente um `EmptyState`. */
  vazio?: React.ReactNode;
  rotuloAcessivel?: string;
}

function ListaSelecao<T>({
  itens,
  chave,
  rotulo,
  detalhe,
  sufixo,
  aoEscolher,
  escolhido,
  vazio = null,
  rotuloAcessivel = 'Opções',
}: PropsListaSelecao<T>): React.ReactElement {
  if (itens.length === 0) return <>{vazio}</>;

  return (
    <ul aria-label={rotuloAcessivel} className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
      {itens.map((item) => {
        const marcado = escolhido?.(item) ?? false;
        const secundaria = detalhe?.(item);

        return (
          <li key={chave(item)}>
            <button
              type="button"
              onClick={() => aoEscolher(item)}
              aria-current={marcado || undefined}
              className={[
                'tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-text-left',
                /* 44px de alvo de toque (spec 01 §7) — a lista é usada no
                   celular, com uma mão só. */
                'tw-min-h-[44px] tw-cursor-pointer tw-rounded-xl tw-border tw-px-4 tw-py-2.5',
                'tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand/40',
                marcado
                  ? 'tw-border-brand tw-bg-brand-tint'
                  : 'tw-border-transparent tw-bg-transparent hover:tw-border-superficie-borda hover:tw-bg-superficie',
              ].join(' ')}
            >
              <span className="tw-min-w-0">
                <span
                  className={[
                    'tw-block tw-truncate tw-text-sm tw-font-semibold',
                    marcado ? 'tw-text-brand' : 'tw-text-texto-titulo',
                  ].join(' ')}
                >
                  {rotulo(item)}
                </span>
                {secundaria && (
                  <span className="tw-block tw-truncate tw-text-xs tw-text-texto-suave">
                    {secundaria}
                  </span>
                )}
              </span>

              {sufixo?.(item) ? (
                <span className="tw-shrink-0 tw-text-sm tw-font-semibold tw-text-texto-corpo">
                  {sufixo(item)}
                </span>
              ) : null}

              {marcado && (
                <span className="tw-shrink-0 tw-text-brand" aria-hidden="true">
                  <Icone nome="check" tamanho={16} traco={2.5} />
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default ListaSelecao;
